import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { find as _find, get as _get } from 'lodash';
import FeatureFlags from 'common/feature_flags';
import { getCurrentUser } from 'common/current_user';
import { assetIsPublic } from 'common/views/helpers';
import Responsive from './lib/Responsive';

import AlertNotice from './components/AlertNotice';
import AssetActionBar from 'common/components/AssetActionBar';
import BlobDownload from './components/BlobDownload';
import BlobPreview from './components/BlobPreview';
import ContactFormNotice from './components/ContactForm/ContactFormNotice';
import DatasetArchives from './components/DatasetArchives';
import DatasetPreview from './components/DatasetPreviewContainer';
import FeaturedContent from './components/FeaturedContent';
import FederatedAssetNotice from './components/FederatedAssetNotice';
import HrefDownload from './components/HrefDownload';
import NewInfoPane from './components/NewInfoPane';
import MetadataTable from './components/MetadataTable';
import RelatedViewList from './components/RelatedViewList';
import RowDetails from './components/RowDetails';
import SchemaPreview from './components/SchemaPreview';
import ScheduleFailureNotice from './components/ScheduleFailureNotice';
import TabViewSwitcher, { DslpTab } from './components/TabViewSwitcher';
import ExportModal, { TOGGLE_OPTIONS } from 'common/components/ExportModal';
import { getQueryStringFromVif, getQueryStringFromView } from 'common/components/ExportModal/ExportHelper';
import { none } from 'ts-option';
import { fetchAIFourfourWhitelist } from 'common/core/configurations';
import AiChatBotComponent, { Mode, RenderMode } from '@tylertech/ai-chatbot-component';
import { getSampleData } from 'common/soql/data-helpers';
import { RowCountProvider } from 'common/providers/RowCount/RowCountProvider';
export class App extends Component {
  constructor(props) {
    super(props);
    this.state = {
      exportModalOpen: false,
      currentVif: null,
      exportModalDefaultToggle: TOGGLE_OPTIONS.DOWNLOAD_FILE,
      aiConfigsFourFours: [],
      aiChatbotContext: null,
      aiChatbotLoading: false
    };
  }

  componentDidMount() {
    this.fetchAiConfigs(this.props.view).then(() => {
      if (this.shouldRenderChatbot()) {
        this.loadAiChatbot();
      }
    });
  }

  fetchAiConfigs = async (view) => {
    try {
      // Updated the logic to fetch the correct 'FxFs' property from AI Functionality Config.
      // Instead of relying on the first property (which could be empty), it now searches for 'FxFs' and returns its value.
      const fourfours = await fetchAIFourfourWhitelist(view.coreView.domainCName).then((response) => {
        const properties = _get(response, '[0].properties', []);
        const fxFsProperty = _find(properties, { name: 'FxFs' });
        return _get(fxFsProperty, 'value', []);
      });
      this.setState({ aiConfigsFourFours: fourfours });
    } catch (error) {
      this.setState({ aiConfigsFourFours: [] });
    }
  };

  renderAssetActionBar() {
    return <AssetActionBar view={this.props.view.coreView} user={getCurrentUser()} />;
  }

  openExportDialog = (exportModalDefaultToggle) => {
    this.setState({ exportModalDefaultToggle: exportModalDefaultToggle });
    this.setState({ exportModalOpen: true });
  };

  closeExportDialog = () => {
    this.setState({ exportModalOpen: false });
    this.setState({ exportModalDefaultToggle: TOGGLE_OPTIONS.DOWNLOAD_FILE });
  };

  onVifUpdate = (vif) => {
    this.setState({ currentVif: vif });
  };

  async createChatBotContext() {
    const { view } = this.props;
    const { coreView } = view;

    let chatBotContext;

    const commonQuestionsMetadataFieldset = _find(view?.customMetadataFieldsets, (fieldSet) => {
      return fieldSet?.name === 'Common Questions';
    });

    const commonQuestionsList =
      commonQuestionsMetadataFieldset?.fields.map(
        (field) => commonQuestionsMetadataFieldset.existing_fields[field.name]
      ) || [];

    if (FeatureFlags.value('use_omnibot_for_data_assistant')) {
      chatBotContext = {
        domainName: coreView.domainCName,
        selectedDatasetId: coreView.id
      };
    } else {
      const sampleData = await getSampleData({
        datasetUid: view.id,
        domain: coreView.domainCName,
        limitRows: 10
      });

      // Get schema information from columns
      const columns = coreView.columns || [];
      const schema = columns.map((column) => ({
        name: column.name,
        dataTypeName: column.dataTypeName,
        fieldName: column.fieldName,
        description: column.description
      }));

      // Format dates safely
      let createdAt = '';
      if (coreView.createdAt) {
        createdAt =
          typeof coreView.createdAt === 'string'
            ? coreView.createdAt
            : new Date(coreView.createdAt).toISOString();
      }

      let updatedAt = '';
      if (coreView.updatedAt) {
        updatedAt =
          typeof coreView.updatedAt === 'string'
            ? coreView.updatedAt
            : new Date(coreView.updatedAt).toISOString();
      }

      chatBotContext = {
        domainName: coreView.domainCName,
        datasetName: coreView.name,
        datasetId: coreView.id,
        datasetUid: view.id,
        sampleData: sampleData,
        schema: schema,
        datasetOwnerName: coreView.owner?.displayName || '',
        datasetCreatedAt: createdAt,
        datasetUpdatedAt: updatedAt,
        viewCount: typeof coreView.viewCount === 'number' ? coreView.viewCount : 0
      };
    }

    if (commonQuestionsList.length > 0) {
      chatBotContext.commonQuestions = commonQuestionsList;
    }

    return chatBotContext;
  }

  shouldRenderChatbot() {
    const {
      view: { coreView }
    } = this.props;
    const enabledByFlag = FeatureFlags.value('enable_ai_chatbot');
    const enabledOnPrivateDatasets = FeatureFlags.value(
      'enable_data_assistant_on_private_and_internal_datasets'
    );

    if (!enabledByFlag) {
      return false;
    } else if (enabledOnPrivateDatasets) {
      return true;
    }

    const currentDatasetIsInAllowList = this.state.aiConfigsFourFours.includes(this.props.view.id);
    return currentDatasetIsInAllowList && assetIsPublic(coreView);
  }

  loadAiChatbot = async () => {
    this.setState({ aiChatbotLoading: true });

    try {
      const chatBotContext = await this.createChatBotContext();

      this.setState({
        aiChatbotContext: chatBotContext,
        aiChatbotLoading: false
      });
    } catch (error) {
      console.error('Error loading AI chatbot context:', error);
      this.setState({ aiChatbotLoading: false });
    }
  };

  renderAiChatbotContainer() {
    const { aiChatbotContext } = this.state;

    if (!this.shouldRenderChatbot()) {
      return null;
    }

    if (aiChatbotContext) {
      const chatbotMode = FeatureFlags.value('use_omnibot_for_data_assistant') ? Mode.OMNI : Mode.SOQL;

      return (
        <AiChatBotComponent
          mode={chatbotMode}
          renderMode={RenderMode.FLOATING}
          appContext={aiChatbotContext}
        />
      );
    }

    return null;
  }

  renderNoticesAndAAB() {
    return (
      <div>
        {this.renderAssetActionBar()}
        <FederatedAssetNotice />
        <AlertNotice />
        <ScheduleFailureNotice />
        <ContactFormNotice />
        {this.state.exportModalOpen && this.renderExportModal()}
      </div>
    );
  }

  renderExportModal() {
    const { view } = this.props;
    const { currentVif } = this.state;
    let queryStringClause;

    if (currentVif) {
      // We only want to set the queryString if it's different from the standard 'select all columns' query
      const coreViewSelectString = getQueryStringFromView(view.coreView);
      const vifQueryString = getQueryStringFromVif(currentVif);
      queryStringClause = vifQueryString !== coreViewSelectString ? vifQueryString : null;
    }

    return (
      <ExportModal
        defaultToggleOption={this.state.exportModalDefaultToggle}
        query={none} // this is a query AST object on explore_grid
        queryStringClause={queryStringClause}
        view={view.coreView}
        bodyText={view.name}
        clientContextVariables={[]}
        fourfour={view.id}
        showDataToggles={true}
        onDismiss={this.closeExportDialog}
      />
    );
  }

  renderFeatureContent() {
    let rowIdentifierName;
    const { coreView } = this.props.view;
    if (coreView.assetType === 'dataset') {
      rowIdentifierName = coreView.columns.find(
        (column) =>
          column.id === coreView.rowIdentifierColumnId &&
          // we want to ensure that the column does not have a 'hidden' flag
          !(column.flags && column.flags.some((flag) => flag === 'hidden'))
      )?.name;
    }

    return (
      <div>
        <FeaturedContent />
        <MetadataTable />
        <RowDetails rowIdentifier={rowIdentifierName} />
        <div data-testid="app-schema-preview">
          <SchemaPreview />
        </div>
      </div>
    );
  }

  renderResponsiveContent() {
    return (
      <div className="container landing-page-container">
        <Responsive>
          <RelatedViewList parentUpdatedAt={this.props.view.lastUpdatedAt} />
        </Responsive>
      </div>
    );
  }

  renderDatasetContainer() {
    return [
      <div key={0}>
        <div className="container landing-page-container">
          <NewInfoPane />
          {this.renderAiChatbotContainer()}
          {this.renderFeatureContent()}
          <Responsive>{FeatureFlags.value('enable_asset_archival') && <DatasetArchives />}</Responsive>
        </div>
      </div>,
      <div key={1}>
        <DatasetPreview onVifUpdate={this.onVifUpdate} />
      </div>,
      <div key={2}>{this.renderResponsiveContent()}</div>
    ];
  }

  renderBlobContainer() {
    return [
      <div key={0}>
        <div className="container landing-page-container">
          <NewInfoPane />
          {this.renderAiChatbotContainer()}
          <BlobPreview />
          <FeaturedContent />
          <BlobDownload />
          <MetadataTable />
        </div>
      </div>
    ];
  }

  renderHrefContainer() {
    return [
      <div key={0}>
        <div className="container landing-page-container">
          <NewInfoPane />
          {this.renderAiChatbotContainer()}
          <FeaturedContent />
          <HrefDownload />
          <MetadataTable />
        </div>
      </div>
    ];
  }

  render() {
    const { view } = this.props;
    let child = null;
    if (view.isBlobby) {
      child = this.renderBlobContainer();
    } else if (view.isHref) {
      child = this.renderHrefContainer();
    } else {
      child = this.renderDatasetContainer();
    }

    const tabFromUrl = () => {
      const path = this.props?.route?.path;
      if (path) {
        const lastSegment = path.substring(path.lastIndexOf('/') + 1);
        // Ensure its a valid tab
        if ([DslpTab.About, DslpTab.Data, DslpTab.RelatedContent].includes(lastSegment)) return lastSegment;
      }
      //default tab
      return DslpTab.About;
    };

    return (
      <RowCountProvider view={view.coreView}>
        {this.renderNoticesAndAAB()}
        <TabViewSwitcher view={view} openExportDialog={this.openExportDialog} currentTab={tabFromUrl()}>
          {child}
        </TabViewSwitcher>
      </RowCountProvider>
    );
  }
}

App.propTypes = {
  view: PropTypes.object,
  params: PropTypes.object
};

function mapStateToProps(state) {
  return _.pick(state, 'view');
}

export default connect(mapStateToProps)(App);
